(ns ui.view.transaction-process.modals.transition.view
  (:require
   [common.localization :refer [tr]]
   [common.util :as cu]
   [re-frame.core :as rf]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.view.transaction-process.modals.transition.event :as event]
   [ui.view.transaction-process.modals.transition.model :as model]
   [ui.view.transaction-process.modals.transition.sub :as sub]
   [ui.view.transaction-process.sub :as tp-sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- field-row
  [{:keys [ctx field-kw field-config field-configs field-vals field-errors show-errors? mandatory?]}]
  (let [data-type (:field/data-type field-config)
        error     (get field-errors field-kw)]
    [:div {:class "flex flex-col gap-1"}
     (if (= data-type :data.type/currency-value)
       (let [sym-kw (cu/currency->symbol field-kw)]
         [labelled-input/currency-view
          {:label          (:field/label field-config)
           :mandatory?     mandatory?
           :data-type      data-type
           :value          (get field-vals field-kw)
           :on-change      (fn [v] (rf/dispatch [::event/set-field-val ctx field-kw v]))
           :secondary-args {:data-type :data.type/currency-symbol
                            :value     (get field-vals sym-kw)
                            :on-change (fn [v] (rf/dispatch [::event/set-field-val ctx sym-kw v]))}}])
       [labelled-input/view
        {:label      (:field/label field-config)
         :mandatory? mandatory?
         :data-type  data-type
         :value      (get field-vals field-kw)
         :options    (:field/options field-config)
         :on-change  (fn [v] (rf/dispatch [::event/set-field-val ctx field-kw v]))}])
     (when (and show-errors? error)
       [:p {:class "text-sm m-0 text-red-600"}
        (tp-util/validation-error-message error field-configs)])]))

(defn view
  [{:keys [ctx]}]
  (let [transition            @(rf/subscribe [::sub/transition ctx])
        template              @(rf/subscribe [::tp-sub/template ctx])
        visible-field-kws     @(rf/subscribe [::sub/visible-field-kws ctx])
        field-vals            @(rf/subscribe [::sub/field-vals ctx])
        field-errors          @(rf/subscribe [::sub/field-errors ctx])
        show-errors?          @(rf/subscribe [::sub/show-errors? ctx])
        confirmed?            @(rf/subscribe [::sub/confirmed? ctx])
        skip-slot-controls    @(rf/subscribe [::sub/skip-slot-controls ctx])
        skip-slot-vals        @(rf/subscribe [::sub/skip-slot-vals ctx])
        missing               @(rf/subscribe [::sub/missing ctx])
        missing-required      @(rf/subscribe [::sub/missing-required ctx])
        required-field-kws    (set @(rf/subscribe [::sub/required-field-kws ctx]))
        blocking-sub-entities @(rf/subscribe [::sub/blocking-sub-entities ctx])
        field-configs         (:template/fields template)
        visible-field-set     (set visible-field-kws)
        general-field-errors  (remove (fn [[field-kw _error]]
                                        (contains? visible-field-set field-kw))
                                      field-errors)
        recipients            @(rf/subscribe [::sub/recipients ctx])
        reject-action?        (= :reject (:transition/action transition))
        backward?             (model/backward-transition? transition)
        destination-label     (->> (:template/stages template)
                                   (filter (fn [stage]
                                             (= (:transition/to transition)
                                                (:stage/id stage))))
                                   first
                                   :stage/label)
        confirmation-text     (cond
                                backward?
                                (tr :transaction-process/backward-transition-confirmation)

                                reject-action?
                                (tr :transaction-process/reject-transaction-confirmation)

                                :else
                                (or (:transition/confirmation transition)
                                    (:transition/close-confirmation transition)))
        action-label          (cond
                                backward?
                                (:transition/backward-label transition)

                                reject-action?
                                (tr :transaction-process/reject-transaction)

                                :else
                                (or (:transition/forward-label transition)
                                    (:transition/close-label transition)))
        needs-confirmation?   (boolean confirmation-text)]
    [modal/view
     {:visible? true
      :hide!    #(rf/dispatch [::event/close ctx])}
     (if backward?
       (tr :transaction-process/backward-transition-title)
       (or action-label (tr :confirm-transition)))
     [:div {:class "flex flex-col gap-4"}
      (when backward?
        [alert/warning {}
         [:p {:class "text-sm m-0"}
          (tr :transaction-process/backward-transition-warning
              [destination-label])]])
      (when (seq recipients)
        [alert/warning {}
         [:p {:class "text-sm m-0 font-medium"}
          (:template/stage-transition-notification-notice template)]
         [:ul {:class "text-sm m-0 pl-4 mt-1 list-disc"}
          (for [{:keys [value label]} recipients]
            ^{:key value}
            [:li label])]])
      (doall
       (for [field-kw visible-field-kws]
         (let [field-config (get-in template [:template/fields field-kw])]
           (when field-config
             ^{:key field-kw}
             [field-row {:ctx          ctx
                         :field-kw     field-kw
                         :field-config field-config
                         :field-configs field-configs
                         :field-vals   field-vals
                         :field-errors field-errors
                         :show-errors? show-errors?
                         :mandatory?   (contains? required-field-kws field-kw)}]))))
      (doall
       (for [{:keys [slot-kw label-key]} skip-slot-controls]
         ^{:key slot-kw}
         [:label {:class "flex flex-row gap-2 items-center cursor-pointer"}
          [checkbox/view
           {:value     (boolean (get skip-slot-vals slot-kw))
            :on-change (fn [value]
                         (rf/dispatch [::event/set-skip-slot ctx slot-kw value]))}]
          [:span {:class "text-sm m-0 text-gray-700"} (tr label-key)]]))
      (when confirmation-text
        [:label {:class "flex flex-row gap-2 items-center cursor-pointer"}
         [checkbox/view
          {:value     confirmed?
           :on-change (fn [v] (rf/dispatch [::event/set-confirmed ctx v]))}]
         [:span {:class "text-sm m-0 text-gray-700"} confirmation-text]])
      (when (and show-errors? (or (seq missing) (seq missing-required) (seq blocking-sub-entities)))
        [:div {:class "border border-red-200 bg-red-50 rounded p-3"}
         [:p {:class "text-sm m-0 font-medium text-red-700 mb-1"}
          (tr :mandatory-fields-missing)]
         [:ul {:class "text-sm m-0 text-red-600 pl-4"}
          (for [{:keys [field-kw label]} (concat missing missing-required)]
            ^{:key (str "mf-" field-kw)}
            [:li label])
          (for [[idx {:keys [label]}] (map-indexed vector blocking-sub-entities)]
            ^{:key (str "sb-" idx)}
            [:li label])]])
      (when (and show-errors? (seq general-field-errors))
        [:div {:class "border border-red-200 bg-red-50 rounded p-3"}
         [:ul {:class "text-sm m-0 text-red-600 pl-4 list-disc"}
          (for [[field-kw error] general-field-errors]
            ^{:key (str "invalid-" field-kw)}
            [:li
             (str (get-in field-configs [field-kw :field/label]) ": "
                  (tp-util/validation-error-message error field-configs))])]])]
     [modal/footer-children-left-and-right
      [[button/secondary-hollow
        {:on-click #(rf/dispatch [::event/close ctx])}
        (tr :cancel)]]
      [[(if (or backward? reject-action?) button/danger-solid button/primary-solid)
        {:on-click  #(rf/dispatch [::event/confirm ctx])
         :disabled? (and needs-confirmation? (not confirmed?))}
        (or action-label (tr :confirm))]]]]))
